import { Separator } from '@radix-ui/react-separator';
import { Button, ButtonProps } from 'fc/components/Button';
import { Input } from 'fc/components/Input';
import {
  MultiSelect,
  MultiSelectDropdownOption,
} from 'fc/components/MultiSelect';
import { ListFilter } from 'lucide-react';

// TODO: This is a copy of MultiSelectDropdownOptions. Need to make this generic for Select and MultiSelect
export type DropdownOption<T> = {
  label: string;
  value: T[keyof T];
  icon?: React.ComponentType<{ className?: string }>;
};

type Filter<T> = {
  name: string;
  type: 'select' | 'multiselect';
  options: DropdownOption<T>[];
};

export type SelectedFilters<T> = {
  [key in Filter<T>['name']]?: DropdownOption<T>['value'][];
};

type TableButton = Pick<ButtonProps, 'variant'> &
  React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string };

export type TableControlProps<T> = {
  enableSearch?: boolean;
  tableTitle: string;
  filters?: Filter<T>[];
  onFilterSelected?: (selectedFilters: SelectedFilters<T>) => void;
  selectedFilters: SelectedFilters<T>;
  searchFor?: string;
  updateSearch?: (value: string) => void;
  tableButtons?: TableButton[];
};

const TableControl = <T,>({
  enableSearch,
  tableTitle,
  filters,
  onFilterSelected,
  selectedFilters,
  searchFor,
  updateSearch,
  tableButtons,
}: TableControlProps<T>) => {
  const updateFilters = (
    column: Filter<T>['name'],
    values: DropdownOption<T>['value'][]
  ) => {
    const newFilters = { ...selectedFilters };
    newFilters[column] = values;
    if (onFilterSelected) {
      onFilterSelected(newFilters);
    }
  };

  return (
    <div className="flex items-center justify-between">
      <h1 className="font-medium text-2xl">{tableTitle}</h1>
      <div className="flex gap-2">
        {enableSearch && (
          <Input
            className="outline-none"
            placeholder="Search"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateSearch && updateSearch(e.target.value)
            }
            value={searchFor ?? ''}
          />
        )}

        {filters &&
          filters.map(
            (filter, key) =>
              filter.type === 'multiselect' && (
                <MultiSelect
                  key={key}
                  options={filter.options as MultiSelectDropdownOption[]}
                  onValueChange={(values) =>
                    updateFilters(filter.name, values as T[keyof T][])
                  }
                  defaultValue={
                    (selectedFilters[filter.name] as string[]) ?? []
                  }
                  animation={2}
                  maxCount={3}
                  searchable
                  showClearAll
                  renderTrigger={(options) => (
                    <Button
                      className="bg-white-100 border-borderGray border-[1px] gap-1 flex items-center"
                      variant="ghost"
                    >
                      <ListFilter size={'1rem'} />
                      <div className="flex items-center">
                        <p>{filter.name}</p>
                        {!!options.length && (
                          <>
                            <Separator
                              orientation="vertical"
                              className="h-8 mx-2"
                            />
                            <span className="bg-background-secondary p-1 px-2">
                              {options.length} selected
                            </span>
                          </>
                        )}
                      </div>
                    </Button>
                  )}
                  className="bg-white"
                />
              )
            // TODO : Need to implement type : select
          )}
        {tableButtons &&
          tableButtons.map((btn, idx) => {
            return (
              <Button key={idx} {...btn}>
                {btn.label}
              </Button>
            );
          })}
      </div>
    </div>
  );
};

export default TableControl;
